import * as queryString from 'query-string';
import * as _ from 'underscore';

import { ExplorableItemDefinitions } from '../models/ContentDefinitions';
import { VizService } from '../services/VizService';

export function isSitePlacePath(path: string): boolean {
  const types: string[] = Object.keys(ExplorableItemDefinitions);
  return new RegExp('^(\/site\/[^\/]+)?\/(' + types.join('|') + ')s(\/)?$').test(path);
}

export function isProjectPlacePath(projectId: string, path: string): boolean {
  return new RegExp('^(\/site\/[^\/]+)?\/projects\/' + projectId + '\/(.*)?').test(path);
}

export function isWorkbookPlacePath(workbookId: string, path: string): boolean {
  return new RegExp('^(\/site\/[^\/]+)?\/workbooks\/' + workbookId + '\/(.*)?').test(path);
}

export function isDatasourcePlacePath(datasourceId: string, path: string): boolean {
  return new RegExp('^(\/site\/[^\/]+)?\/datasources\/' + datasourceId + '\/(.*)?').test(path);
}

export function isFlowPlacePath(flowId: string, path: string): boolean {
  return new RegExp('^(\/site\/[^\/]+)?\/flows\/' + flowId + '\/(.*)?').test(path);
}

export function isDataRolePlacePath(roleId: string, path: string): boolean {
  return new RegExp('^(\/site\/[^\/]+)?\/dataRoles\/' + roleId + '\/(.*)?').test(path);
}

export function getPathFromUrl(url: string): string {
  if (!url) {
    return url;
  }

  const hashPrefix = '#';
  const queryStrPrefix = '?';

  const start = url.includes(hashPrefix) ? url.indexOf(hashPrefix) + 1 : 0;
  const end = url.includes(queryStrPrefix, start) ? url.indexOf(queryStrPrefix, start) : url.length;

  return url.slice(start, end);
}

export function isCurrentUrlViz(): boolean {
  const currentUrl = window.location.href;
  return isVizUrl(currentUrl);
}

export function isVizUrl(url: string): boolean {
  const regExp = /[^/]\/(views)\/([^?#]*)/;
  const match = url.match(regExp);
  return !!(match && match[2]);
}

export function isSearchSpaceUrl(url: string): boolean {
  const regExp = /\/search/g;
  const match = url.match(regExp);
  return !!(match && match[0]) && !isVizUrl(url);
}

export function shouldSetPreviousUrl(oldUrl: string, newUrl: string, vizService: VizService): boolean {
  const fromNotSearchToSearch = !isSearchSpaceUrl(oldUrl) && isSearchSpaceUrl(newUrl);
  const fromSearchToSearch = isSearchSpaceUrl(oldUrl) && isSearchSpaceUrl(newUrl);
  const notVizUrl = !vizService.isRedirectingToVizUrl(oldUrl) && !isVizUrl(oldUrl);

  // set the previous url if we're not coming from viz or redirecting to a viz
  // Or we have navigating to and from the search space
  return (notVizUrl && !fromSearchToSearch) || fromNotSearchToSearch;
}

export interface ShouldResetPreviousUrlParams {
  toState: ng.ui.IState;
  toStateParams: any;
  fromState: ng.ui.IState;
  fromStateParams: any;
  currentPath: string;
}

export function shouldResetPreviousUrl(params: ShouldResetPreviousUrlParams) {
    const {toState, toStateParams, fromState, fromStateParams, currentPath} = params;
    // We don't want to keep the error or login state or when we switch sites
    return isErrorState(fromState) || isLoginInPath(currentPath) || isChangingSites(toStateParams, fromStateParams);
  }

function isChangingSites(toStateParams: any, fromStateParams: any) {
  return toStateParams.siteUrlName !== fromStateParams.siteUrlName;
}

export function isLoginInPath(path: string): boolean {
  return path === '/signin';
}

function isErrorState(state: ng.ui.IState): boolean {
  return state && state.name === 'main.error';
}

export function getQueryFromUrl(url: string) {
  const queryIndex = url.lastIndexOf('?');
  // so that we don't parse out the feature flags if there are no query params.
  const hashIndex = url.indexOf('#');

  let query = '';
  if (queryIndex > -1 && queryIndex > hashIndex) {
    query = url.substring(queryIndex);
  }
  // this will return an empty object if the parse goes awry.
  return queryString.parse(query);
}

/**
 * Gets the site urlName from the current url if it is the login state
 */
export function getSiteUrlNameIfOnSigninPath(path: string, searchParams: {}): string {
  if (path.startsWith('/signin') && _.has(searchParams, 'site')) {
    return (searchParams as any).site;
  }
  return null;
}
